import React, { useMemo } from 'react';
import { isEmpty, isNil } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { setDataMistakesModalMessage } from '../../../../../actions/chatActions.js';
import { Loader } from '../../../../Common/index.js';
import { useGetDataMistakeTypesQuery, useRecordDataMistakeMutation } from '../../../../../api/messagesApi.js';
import { Button, Stack, Typography, Select, MenuItem, Modal, Paper, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { dataMistakesModalMessageSelector } from '../../../../../selectors/chatSelectors.js';

/** Max comment size in db */
const MAX_COMMENT_LENGTH = 2000;

const DEFAULT_VALUES = {
  dataMistakeType: '',
  comments: ''
};

const DataMistakesModal = () => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({ defaultValues: DEFAULT_VALUES });

  const { data: dataMistakeTypes = [] } = useGetDataMistakeTypesQuery();

  const [recordDataMistake] = useRecordDataMistakeMutation();

  const dataMistakesModalMessage = useSelector(dataMistakesModalMessageSelector);

  const dispatch = useDispatch();

  const dataMistakeCategory = useMemo(
    () => (dataMistakesModalMessage?.textType?.includes('assistant') ? 'ai' : 'write-off'),
    [dataMistakesModalMessage]
  );

  const categorySelectOptions = useMemo(() => {
    if (isEmpty(dataMistakeTypes)) {
      return [];
    }
    return dataMistakeTypes
      .filter((mistakeType) => mistakeType.category === dataMistakeCategory)
      .map(({ id, name }) => ({
        value: id,
        label: name
      }));
  }, [dataMistakeTypes, dataMistakeCategory]);

  const closeModal = () => {
    reset(DEFAULT_VALUES);
    dispatch(setDataMistakesModalMessage(null));
  };

  const onSubmit = async (values) => {
    await recordDataMistake({
      mistakeTypeId: values.dataMistakeType,
      smsId: dataMistakesModalMessage.id,
      comments: values.comments
    });
    closeModal();
  };

  return (
    <Modal open={!isNil(dataMistakesModalMessage)} onClose={closeModal}>
      <Paper sx={{ p: 3, maxWidth: 800, maxHeight: 800, overflow: 'auto', margin: 'auto', marginTop: '10vh' }}>
        <Typography variant='h5'>Report a Data Mistake 🐛🐛🐛</Typography>
        <Stack
          sx={{
            fontSize: '20px',
            flexDirection: 'row',
            alignItems: 'center',
            p: 1
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{
              width: '250px',
              flexShrink: 0
            }}
          >
            Response
          </Typography>
          <Typography
            variant='body2'
            sx={[
              {
                fontSize: '14px',
                whiteSpace: 'pre-wrap',
                width: '450px'
              },
              (theme) => ({
                wordWrap: 'break-word',
                color: theme.palette.outerSpace.main
              })
            ]}
          >
            {dataMistakesModalMessage?.body}
          </Typography>
        </Stack>
        <Stack
          sx={{
            fontSize: '20px',
            flexDirection: 'row',
            alignItems: 'center',
            p: 1
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{
              width: '250px',
              flexShrink: 0
            }}
          >
            Mistake Type
          </Typography>
          <Controller
            name='dataMistakeType'
            control={control}
            rules={{ required: 'A mistake type must be provided' }}
            render={({ field }) => (
              <Stack sx={{ width: '450px' }}>
                <Select
                  {...field}
                  error={!!errors.dataMistakeType}
                  sx={{
                    fontSize: '14px'
                  }}
                >
                  {categorySelectOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.dataMistakeType && (
                  <Typography variant='body2' color='error' sx={{ mt: 0.5 }}>
                    {errors.dataMistakeType.message}
                  </Typography>
                )}
              </Stack>
            )}
          />
        </Stack>
        <Stack
          sx={{
            fontSize: '20px',
            flexDirection: 'row',
            alignItems: 'center',
            p: 1
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{
              width: '250px',
              flexShrink: 0
            }}
          >
            Comments / Suggestions
          </Typography>
          <Controller
            name='comments'
            control={control}
            rules={{
              required: 'A comment must be provided',
              maxLength: {
                value: MAX_COMMENT_LENGTH,
                message: `Comment must be ${MAX_COMMENT_LENGTH} chars or shorter`
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                minRows={5}
                maxRows={5}
                error={!!errors.comments}
                helperText={errors.comments?.message}
                sx={{ width: '450px' }}
              />
            )}
          />
        </Stack>
        <Stack
          sx={{
            pt: 2,
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {!isSubmitting && (
            <Button variant='contained' color='primary' onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
              Submit
            </Button>
          )}
          {isSubmitting && <Loader />}
        </Stack>
      </Paper>
    </Modal>
  );
};

export default DataMistakesModal;
